// Admin login page. Authenticates with Firebase email/password, then verifies
// the account is an admin (users/{uid}/role === "admin"). Non-admins are
// signed straight back out and shown an error.
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import { useAuth, checkIsAdmin } from './AuthContext';
import './AdminLogin.css';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, loading } = useAuth();

  const from = (location.state as any)?.from?.pathname || '/admin';

  // Already-authenticated admins skip the form.
  useEffect(() => {
    if (!loading && user && isAdmin) {
      navigate(from, { replace: true });
    }
  }, [loading, user, isAdmin, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const em = email.trim();
    const pw = password.trim();
    if (!em) return setError('Email cannot be empty');
    if (!pw) return setError('Password cannot be empty');

    setBusy(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, em, pw);
      const admin = await checkIsAdmin(cred.user.uid);
      if (!admin) {
        await auth.signOut();
        setError('This account is not an admin.');
        return;
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        setError('Invalid email or password.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Try again later.');
      } else {
        setError('Login failed: ' + (err?.message || 'unknown error'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={handleSubmit}>
        <h1>Admin sign in</h1>
        <p className="login-sub">Camplocation OSM import tool</p>

        <label>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@camplocation.com"
          autoComplete="username"
        />

        <label>Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          autoComplete="current-password"
        />

        {error && <div className="login-error">{error}</div>}

        <button type="submit" className="login-btn" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

export default AdminLogin;
